/**
 * Authentication for the admin console.
 *
 * The important difference from the tenant app: signing in successfully is not
 * enough. This console is only for platform staff, so a user without
 * `isPlatformAdmin` is signed out again and told to use the main application.
 *
 * That check is a courtesy, not the security boundary — every administrative
 * endpoint independently verifies the claim on the token. A non-admin who
 * bypassed this screen would simply get 403s.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as adminApi from '../services/index.js';
import { onSessionExpired } from '../lib/apiClient.js';
import { clearTokens, getRefreshToken, setTokens } from '../lib/tokenStore.js';

const AdminAuthContext = createContext(null);

/** Thrown when a valid sign-in belongs to someone who is not platform staff. */
export class NotPlatformAdminError extends Error {
  constructor() {
    super('This console is for platform administrators. Use the main application to access your organization.');
    this.name = 'NotPlatformAdminError';
  }
}

/**
 * Thrown when a platform administrator still holds a temporary password.
 *
 * This console has no first-login flow of its own, because platform staff are
 * created by the seed or the bootstrap endpoint with a password they chose
 * themselves. It only happens to an account minted through the IAM API without
 * one, so the useful thing to do is say so plainly rather than offer a screen
 * that exists for one edge case.
 */
export class PasswordChangeRequiredError extends Error {
  constructor() {
    super(
      'This account is still on a temporary password. Change it in the main ERP application at /change-password, then sign in here.'
    );
    this.name = 'PasswordChangeRequiredError';
  }
}

export function AdminAuthProvider({ children }) {
  const [status, setStatus] = useState('loading');
  const [user, setUser] = useState(null);

  const signOutLocally = useCallback(() => {
    clearTokens();
    setUser(null);
    setStatus('ready');
  }, []);

  // Restore a session on load.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!getRefreshToken()) {
        setStatus('ready');
        return;
      }

      try {
        const profile = await adminApi.me();
        if (cancelled) return;

        // A stored session that is not an admin session — or is restricted to
        // changing a password — is no use here.
        if (!profile?.isPlatformAdmin || profile.mustChangePassword) {
          clearTokens();
          setUser(null);
        } else {
          setUser(profile);
        }
      } catch {
        clearTokens();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setStatus('ready');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => onSessionExpired(signOutLocally), [signOutLocally]);

  const signIn = useCallback(async (credentials) => {
    const result = await adminApi.login(credentials);

    if (result.requiresPasswordChange || result.user?.mustChangePassword) {
      // The token we were handed is scoped to the password-change endpoints,
      // so every administrative call would 403. Discard it.
      clearTokens();
      throw new PasswordChangeRequiredError();
    }

    if (!result.user?.isPlatformAdmin) {
      // Credentials were correct, but this is the wrong console. Do not keep
      // the tokens: a non-admin session has no use here.
      clearTokens();
      throw new NotPlatformAdminError();
    }

    setTokens({ accessToken: result.accessToken, refreshToken: result.refreshToken });
    setUser(result.user);
    setStatus('ready');
    return result.user;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await adminApi.logout(getRefreshToken());
    } catch {
      // Best effort; the local session ends either way.
    }
    signOutLocally();
  }, [signOutLocally]);

  const value = useMemo(
    () => ({ status, user, isAuthenticated: Boolean(user), signIn, signOut }),
    [status, user, signIn, signOut]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) throw new Error('useAdminAuth must be used inside an AdminAuthProvider');
  return context;
}
